import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ScrollView } from 'tamagui'
import PlaceIcon from '@mui/icons-material/Place'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import MapIcon from '@mui/icons-material/Map'
import ShareIcon from '@mui/icons-material/Share'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import {
  usePlaceQuery,
  usePlaceCommentsQuery,
  useToggleLikeMutation,
  useToggleBeenMutation,
  usePlaceReactionPeopleQuery,
} from '@cobu/api-client'
import { CobuBackButton, CobuContainer, CobuHeader, CobuStack, CobuText, CobuTextAvatar, EventDetailSkeleton } from '@cobu/ui'
import type { PlaceShareType } from '@cobu/types'
import { RootState } from '../../store'
import { PlaceNeighborChat } from '../../components/places/PlaceNeighborChat'
import FloatingCommentButton from '../../components/ui/FloatingCommentButton'
import { ShareToFeedSheet } from '../../components/places/ShareToFeedSheet'
import { PeopleListSheet } from '../../components/places/PeopleListSheet'
import PlaceMenuButton from '../../components/places/PlaceMenuButton'
import { ReportPlaceModal } from '../../components/places/ReportPlaceModal'
import { CATEGORY_META, getCategoryLabel } from '../../components/places/categoryMeta'
import { formatPlaceAddressLine } from '../../components/places/placeFormat'
import { formatRelativeTime } from '../../utils/dateTime'
import { showErrorMessage } from '../../utils/messageToast'
import { Strings } from '../../constants/Strings'

const QUICK_PROMPTS: Array<{ key: 'wantToGo' | 'question' | 'recommend'; shareType: PlaceShareType }> = [
  { key: 'wantToGo', shareType: 'want_to_go' },
  { key: 'question', shareType: 'question' },
  { key: 'recommend', shareType: 'recommend' },
]

export default function PlaceDetailScreen() {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const placeId = params.id || ''
  const user = useSelector((state: RootState) => state.auth.user)

  const { data: place, isLoading, isError } = usePlaceQuery(placeId)
  const { data: comments = [] } = usePlaceCommentsQuery(placeId)
  const toggleLike = useToggleLikeMutation(placeId)
  const toggleBeen = useToggleBeenMutation(placeId)

  const [shareOpen, setShareOpen] = useState(false)
  const [shareType, setShareType] = useState<PlaceShareType>('want_to_go')
  const [reportOpen, setReportOpen] = useState(false)
  const [peopleSheet, setPeopleSheet] = useState<'like' | 'been' | null>(null)

  const peopleQuery = usePlaceReactionPeopleQuery(placeId, peopleSheet ?? 'like', peopleSheet !== null)
  const strings = Strings.get().places

  const openShare = (type: PlaceShareType = 'want_to_go') => {
    setShareType(type)
    setShareOpen(true)
  }

  if (isLoading) {
    return <EventDetailSkeleton />
  }

  if (isError || !place) {
    showErrorMessage(strings.detail.notFound)
    return null
  }

  const addressLine = formatPlaceAddressLine(place)
  const hasTypedAddress = !!(place.street || place.city || place.zip)
  // Prefer the pasted Maps link; otherwise build a query from the typed address.
  // A place with only a Maps link (building has no address, so distance is null
  // and there's no street) still gets a usable link here.
  const mapsUrl = place.mapsLink
    || (hasTypedAddress
      ? `https://maps.google.com/?q=${encodeURIComponent(`${place.street}, ${place.city} ${place.zip}`)}`
      : '')
  // With no typed address and no distance, fall back to an explicit label so the
  // (still tappable) Maps link is never a blank line.
  const addressLabel = addressLine || (mapsUrl ? strings.detail.openInMaps : '')
  const CategoryIcon = CATEGORY_META[place.category].icon

  return (
    <CobuContainer f={1} bg="$background">
      <CobuHeader
        title={place.name}
        renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
        renderRight={() => <PlaceMenuButton onReportPress={() => setReportOpen(true)} />}
      />

      <ScrollView showsVerticalScrollIndicator={false}>
        <CobuStack p="$4" gap="$3" bg="$backgroundSecondary">
          <CobuStack layout="horizontal" gap="$3">
            <CobuStack w={64} h={64} br="$3" ai="center" jc="center" bg="$background" flexShrink={0}>
              <CategoryIcon style={{ fontSize: 22, opacity: 0.4 }} />
            </CobuStack>

            <CobuStack f={1} gap="$1" jc="center">
              <CobuText variant="body" fow="600" fontSize="$4" numberOfLines={2}>
                {place.name}
              </CobuText>
              <CobuText variant="caption" color="$colorSecondary">
                {getCategoryLabel(place.category)}
              </CobuText>
            </CobuStack>
          </CobuStack>

          {addressLabel ? (
            <CobuStack
              layout="horizontal"
              ai="center"
              gap="$1"
              onPress={mapsUrl ? () => window.open(mapsUrl, '_blank') : undefined}
            >
              <PlaceIcon style={{ fontSize: 16 }} />
              <CobuText
                variant="body"
                fontSize="$3"
                color={mapsUrl ? '$textLinkColor' : '$colorSecondary'}
                textDecorationLine={mapsUrl ? 'underline' : 'none'}
              >
                {addressLabel}
              </CobuText>
            </CobuStack>
          ) : null}

          {place.description ? (
            <CobuStack pl="$3" borderLeftWidth={2} borderColor="$borderColor">
              <CobuText variant="body" fontSize="$3" fontStyle="italic" color="$colorSecondary">
                "{place.description}"
              </CobuText>
            </CobuStack>
          ) : null}

          <CobuStack layout="horizontal" ai="center" gap="$2" mt="$1">
            <CobuTextAvatar
              uuid={place.createdBy}
              name={place.addedByName}
              size="$8"
              fontSize="$2"
            />
            <CobuStack>
              <CobuText variant="body" fontWeight="600" fontSize="$3">
                {Strings.format(strings.detail.addedBy, { name: place.addedByName })}
              </CobuText>
              <CobuText variant="caption" fontSize="$2" color="$colorSecondary">
                {formatRelativeTime(place.createdAt)}
              </CobuText>
            </CobuStack>
          </CobuStack>
        </CobuStack>

        <CobuStack layout="horizontal" gap="$2" px="$4" pb="$3">
          <EngagementButton
            active={place.myLike}
            count={place.likeCount}
            label={strings.detail.likes}
            icon={place.myLike ? <FavoriteIcon style={{ fontSize: 18, color: '#EF4A4A' }} /> : <FavoriteBorderIcon style={{ fontSize: 18 }} />}
            onToggle={() => toggleLike.mutate({ liked: !place.myLike })}
            onOpenList={() => setPeopleSheet('like')}
          />
          <EngagementButton
            active={place.myBeen}
            count={place.beenCount}
            label={strings.detail.been}
            icon={<MapIcon style={{ fontSize: 18 }} />}
            onToggle={() => toggleBeen.mutate({ been: !place.myBeen })}
            onOpenList={() => setPeopleSheet('been')}
          />
          <ActionButton
            label={strings.detail.share}
            icon={<ShareIcon style={{ fontSize: 18 }} />}
            onPress={() => openShare('want_to_go')}
          />
          <ActionButton
            label={strings.detail.comment}
            icon={<ChatBubbleOutlineIcon style={{ fontSize: 18 }} />}
            onPress={() => navigate(`/places/${placeId}/compose-comment`)}
          />
        </CobuStack>

        <CobuStack layout="horizontal" gap="$2" px="$4" pb="$2" style={{ overflowX: 'auto' }}>
          {QUICK_PROMPTS.map(({ key, shareType: type }) => (
            <CobuStack
              key={type}
              px="$3"
              py="$2"
              br="$full"
              bg="$backgroundSecondary"
              onPress={() => openShare(type)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <CobuText variant="caption" fontWeight="500">{strings.detail.quickPrompts[key]}</CobuText>
            </CobuStack>
          ))}
        </CobuStack>

        <PlaceNeighborChat placeId={placeId} comments={comments} currentUserId={user?.uuid} />
      </ScrollView>

      <FloatingCommentButton
        onPress={() => navigate(`/places/${placeId}/compose-comment`)}
        text={strings.chat.addComment}
      />

      <ShareToFeedSheet
        place={place}
        open={shareOpen}
        onOpenChange={setShareOpen}
        initialShareType={shareType}
      />

      <PeopleListSheet
        open={peopleSheet !== null}
        onOpenChange={(o) => !o && setPeopleSheet(null)}
        title={peopleSheet === 'been'
          ? Strings.format(strings.people.beenTitle, { count: String(place.beenCount) })
          : Strings.format(strings.people.likesTitle, { count: String(place.likeCount) })}
        people={peopleQuery.data ?? []}
        loading={peopleQuery.isLoading}
      />

      {user && (
        <ReportPlaceModal
          visible={reportOpen}
          onClose={() => setReportOpen(false)}
          placeId={placeId}
          placeName={place.name}
          reportedByUuid={user.uuid}
        />
      )}
    </CobuContainer>
  )
}

function ActionButton({
  active,
  label,
  icon,
  onPress,
}: {
  active?: boolean
  label: string
  icon: React.ReactNode
  onPress: () => void
}) {
  return (
    <CobuStack
      f={1}
      ai="center"
      gap="$1"
      py="$2"
      br="$3"
      bg={active ? '$backgroundSecondary' : 'transparent'}
      borderWidth={1}
      borderColor="$borderColor"
      onPress={onPress}
    >
      {icon}
      <CobuText variant="caption" fontWeight="600">{label}</CobuText>
    </CobuStack>
  )
}

/**
 * Like/Been button. The icon is its own tap target that toggles the current
 * user's state; the "{count} likes/been" text below is a separate tap target
 * that opens the people-list bottom sheet — the two never overlap, so there's
 * no ambiguity about which tap does what.
 */
function EngagementButton({
  active,
  count,
  label,
  icon,
  onToggle,
  onOpenList,
}: {
  active?: boolean
  count: number
  label: string
  icon: React.ReactNode
  onToggle: () => void
  onOpenList: () => void
}) {
  return (
    <CobuStack
      f={1}
      br="$3"
      bg={active ? '$backgroundSecondary' : 'transparent'}
      borderWidth={1}
      borderColor="$borderColor"
      style={{ overflow: 'hidden' }}
    >
      <CobuStack ai="center" jc="center" py="$2" onPress={onToggle}>
        {icon}
      </CobuStack>
      <CobuStack ai="center" jc="center" pb="$2" onPress={onOpenList}>
        <CobuText variant="caption" fow="600">
          {count} {label}
        </CobuText>
      </CobuStack>
    </CobuStack>
  )
}
